import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  type ReportingV2Grouping,
  selectReportingV2Filters,
  setReportingV2Filters,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS } from '../theme';

/**
 * #4109 asks to rename DSP to "Platform" and, separately, to drop "By Product"
 * and "By DSP" — with a note that they're useful to planners and might be better
 * behind a permission than removed outright. Taking the note: the platform
 * grouping stays (renamed), and Product is gated rather than deleted, so
 * re-enabling it is a flag flip and not a code change. Grouping VALUES are
 * unchanged ('dsp' still goes to the backend) — this is display only.
 */
const SHOW_PRODUCT_GROUPING = false;

/**
 * #4442/#4623 — a user without "Show DSP / Platform" doesn't get the By
 * Platform option at all, not just a disabled one. Exported standalone so the
 * decision is unit-testable without mounting the control (see
 * GroupingControl.test.ts).
 */
export const buildGroupingOptions = (canViewPlatform: boolean): { value: ReportingV2Grouping; label: string }[] => [
  { value: 'all', label: 'All combined' },
  ...(canViewPlatform ? [{ value: 'dsp' as ReportingV2Grouping, label: 'By Platform' }] : []),
  { value: 'channel', label: 'By Channel' },
  ...(SHOW_PRODUCT_GROUPING ? [{ value: 'product' as ReportingV2Grouping, label: 'By Product' }] : []),
];

/** Outlined grouping tabs matching the flank-20 reference (no orange fill). */
const GroupingControl = () => {
  const dispatch = useAppDispatch();
  const { grouping } = useAppSelector(selectReportingV2Filters);
  const { canViewPlatform } = useReportingPermissions();
  const OPTIONS = buildGroupingOptions(canViewPlatform);

  return (
    <ToggleButtonGroup
      size='small'
      exclusive
      value={grouping}
      onChange={(_event, next: ReportingV2Grouping | null) => {
        if (next) dispatch(setReportingV2Filters({ grouping: next }));
      }}
      sx={{
        gap: 0.5,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '8px !important',
          px: 1.5,
          py: 0.4,
          fontSize: '0.8rem',
          color: COLORS.muted,
          fontWeight: 500,
          bgcolor: '#fff',
          '&:hover': { bgcolor: COLORS.pageBg, borderColor: COLORS.muted },
          '&.Mui-selected': {
            bgcolor: '#fff',
            color: COLORS.text,
            borderColor: COLORS.text,
            fontWeight: 700,
            '&:hover': { bgcolor: COLORS.pageBg },
          },
        },
      }}
    >
      {OPTIONS.map(option => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default GroupingControl;
